import type { LegendPointOfInterest } from "@gettactic/map-builder/src/components/ZoneLegend/ZoneLegend";
import {
  type GroupShape,
  type IconShape,
  type Shape,
  type SpotShape,
  SpotShapeType,
  type ZoneShape,
  type ZoneTypeDefinition
} from "@gettactic/map-builder/src/types";
import { computeZoneAreaPx } from "@gettactic/map-builder/src/utils/zonePolygon";

export interface LegendData {
  /** Categories that paint zones on this map (icon-only ones are points
   *  of interest instead). */
  zoneCategoryIds: Set<string>;
  pointsOfInterest: LegendPointOfInterest[];
  linkedZones: ZoneShape[];
  /** Per-category m²; null when uncalibrated or not an admin. */
  zoneAreasByCategory: Map<string, number> | null;
  zoneResourceCountsByCategory: Map<
    string,
    { desks: number; meetingRooms: number }
  >;
}

/** Visits every visible shape, into groups and spot companions. */
const walk = (list: Shape[], visit: (shape: Shape) => void) => {
  for (const item of list) {
    if (item.hidden) continue;
    visit(item);
    if (item.type === "group") {
      walk((item as GroupShape).children || [], visit);
    } else if (item.type === "spot") {
      walk(((item as SpotShape).companions as Shape[]) || [], visit);
    }
  }
};

/**
 * What the legend shows for a document: the same tallies `ViewSVG`
 * computes, gathered in one pass so the SVG viewer's legend matches the
 * current one entry for entry.
 */
export const computeLegendData = (
  shapes: Shape[],
  zoneTypes: ZoneTypeDefinition[],
  options: { pixelsPerMeter: number | null; isAdmin: boolean }
): LegendData => {
  const zoneCategoryIds = new Set<string>();
  const poiByCategory = new Map<string, { count: number; sample: IconShape }>();
  const linkedZones: ZoneShape[] = [];
  const areas = new Map<string, number>();
  const counts = new Map<string, { desks: number; meetingRooms: number }>();
  const spotsById = new Map<string, SpotShape>();
  const zones: ZoneShape[] = [];
  const canMeasure = !!options.pixelsPerMeter && options.isAdmin;

  walk(shapes, (shape) => {
    if (shape.type === "spot") {
      spotsById.set(shape.id, shape as SpotShape);
      return;
    }
    if (shape.type === "icon") {
      const icon = shape as IconShape;
      if (!icon.categoryTypeId) return;
      const entry = poiByCategory.get(icon.categoryTypeId);
      if (entry) entry.count += 1;
      else poiByCategory.set(icon.categoryTypeId, { count: 1, sample: icon });
      return;
    }
    if (shape.type !== "zone") return;
    const zone = shape as ZoneShape;
    zones.push(zone);
    if (zone.link) linkedZones.push(zone);
    if (!zone.categoryTypeId) return;
    zoneCategoryIds.add(zone.categoryTypeId);
    if (canMeasure && options.pixelsPerMeter) {
      const areaSqm = computeZoneAreaPx(zone) / options.pixelsPerMeter ** 2;
      areas.set(
        zone.categoryTypeId,
        (areas.get(zone.categoryTypeId) || 0) + areaSqm
      );
    }
  });

  // Counts resolve contained spots after the walk, so order does not matter.
  for (const zone of zones) {
    if (!zone.categoryTypeId || !zone.containedSpotIds?.length) continue;
    const entry = counts.get(zone.categoryTypeId) || {
      desks: 0,
      meetingRooms: 0
    };
    for (const spotId of zone.containedSpotIds) {
      const spot = spotsById.get(spotId);
      if (!spot) continue;
      if (
        spot.spotType === SpotShapeType.Desk ||
        spot.spotType === SpotShapeType.Node
      ) {
        entry.desks += 1;
      } else if (spot.spotType === SpotShapeType.Room) {
        entry.meetingRooms += 1;
      }
    }
    counts.set(zone.categoryTypeId, entry);
  }

  return {
    zoneCategoryIds,
    pointsOfInterest: zoneTypes
      .filter((type) => poiByCategory.has(type.id))
      .map((type) => {
        const entry = poiByCategory.get(type.id) as {
          count: number;
          sample: IconShape;
        };
        return { category: type, count: entry.count, sample: entry.sample };
      }),
    linkedZones,
    zoneAreasByCategory: canMeasure ? areas : null,
    zoneResourceCountsByCategory: counts
  };
};
